import { useRef, useState } from "react";
import { useSession } from "../../state/SessionProvider";
import { sidenavn, type SideId } from "../../state/types";
import { notch } from "../../components/haptics";
import { Switch } from "../../components/Switch";

/**
 * Sidelista.
 *
 * Rekkefølgen i listen er rekkefølgen i swipen, og den øverste aktiverte er
 * startsiden. Ingen egen innstilling for startside.
 *
 * Avslåtte sider blir liggende i listen, dempet, men de flytter seg ikke
 * automatisk ned — rekkefølgen er brukerens, ikke systemets.
 *
 * LISTEN VISER ALLE SIDENE, også de som er tomme og derfor ikke står i
 * swipen. Det er her valget tas, og et valg man ikke kan ta om fordi
 * flaten er tom i dag ville vært en felle: lastes dagsplanen opp klokka
 * fem, skal sida komme fram av seg selv.
 */
export function PagesPane(): JSX.Element {
  const { pages, setPages } = useSession();
  const [drag, setDrag] = useState<{ from: number; to: number; dy: number } | null>(null);
  const rows = useRef<Map<SideId, HTMLDivElement>>(new Map());
  const originY = useRef(0);
  /*
   * Radenes plassering slik den var da draget startet.
   *
   * Alt regnes mot denne, aldri mot getBoundingClientRect underveis. Under
   * draget er radene flyttet med transform, og da svarer rect-en med den
   * flyttede posisjonen: den loeftede raden fikk dy lagt til to ganger, og
   * shift regnet avstander mellom rader som selv var i bevegelse. Verdiene
   * paavirket sin egen neste maaling, og lista hoppet.
   *
   * Maalt én gang, staar stille. Alle radene er like hoeye, saa det holder.
   */
  const layout = useRef<readonly { top: number; height: number }[]>([]);

  const enabledCount = pages.filter((p) => p.enabled).length;

  const start = (e: React.PointerEvent<HTMLSpanElement>, i: number): void => {
    e.preventDefault();
    // Kaster uten aktiv peker. Uten vakt avbroet den foer setDrag,
    // og raden nektet aa flytte seg uten et tegn.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ingen aktiv peker */ }
    originY.current = e.clientY;
    // Ingenting er flyttet ennaa, saa dette er den rene layouten.
    layout.current = pages.map((p) => {
      const b = rows.current.get(p.page)?.getBoundingClientRect();
      return { top: b?.top ?? 0, height: b?.height ?? 0 };
    });
    setDrag({ from: i, to: i, dy: 0 });
  };

  const move = (e: React.PointerEvent<HTMLSpanElement>): void => {
    if (drag === null) return;
    const base = layout.current[drag.from];
    if (base === undefined) return;
    const dy = e.clientY - originY.current;
    const centre = base.top + base.height / 2 + dy;

    let to = drag.from;
    layout.current.forEach((g, i) => {
      const mid = g.top + g.height / 2;
      if (i < drag.from && centre < mid) to = Math.min(to, i);
      else if (i > drag.from && centre > mid) to = Math.max(to, i);
    });
    // Ett hakk naar raden bytter plass, slik hjulene gjoer.
    if (to !== drag.to) notch();
    setDrag({ from: drag.from, to, dy });
  };

  const end = (): void => {
    if (drag === null) return;
    if (drag.from !== drag.to) {
      const next = pages.slice();
      const [moved] = next.splice(drag.from, 1);
      if (moved !== undefined) next.splice(drag.to, 0, moved);
      setPages(next);
    }
    setDrag(null);
    layout.current = [];
  };

  const shift = (i: number): string => {
    if (drag === null || i === drag.from) return "";
    const { from, to } = drag;
    let slot = i;
    if (from < to && i > from && i <= to) slot = i - 1;
    else if (to < from && i >= to && i < from) slot = i + 1;
    if (slot === i) return "";
    const a = layout.current[i];
    const b = layout.current[slot];
    if (a === undefined || b === undefined) return "";
    return `translateY(${b.top - a.top}px)`;
  };

  return (
    <>
      <span className="mono" style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ink-dim)" }}>
        Rekkefølgen i listen er rekkefølgen i swipen. Den øverste aktiverte er
        startsiden. Dra i håndtaket til venstre for å flytte en rad.
        {" "}
        {/*
          * HVORFOR EN SIDE DU HAR SLAATT PAA LIKEVEL IKKE STAAR DER.
          *
          * Flata skjules naar den er tom — se `pages` i App.tsx. Uten en
          * setning her ser det ut som at bryteren ikke virker.
          */}
        En side uten innhold vises ikke, og kommer tilbake av seg selv når
        noe lastes opp.
      </span>

      <div className="menu__group">
        {pages.map((p, i) => {
          const held = drag?.from === i;
          // Siste aktiverte side kan ikke skrus av — appen må ha en flate.
          const lockedOn = p.enabled && enabledCount < 2;
          return (
            <div
              key={p.page}
              ref={(el) => {
                // Noekkel paa siden og ikke paa indeksen. Indeksene bytter
                // plass naar lista stokkes om, og da kunne en rad slette
                // oppslaget en annen nettopp hadde satt.
                if (el === null) rows.current.delete(p.page);
                else rows.current.set(p.page, el);
              }}
              className={[
                "pagerow",
                held ? "pagerow--held" : "",
                p.enabled ? "" : "pagerow--off",
              ].filter(Boolean).join(" ")}
              style={{ transform: held ? `translateY(${drag.dy}px) scale(1.015)` : shift(i) }}
            >
              <span
                className="pagerow__grip"
                onPointerDown={(e) => start(e, i)}
                onPointerMove={move}
                onPointerUp={end}
                onPointerCancel={end}
                aria-label={`Flytt ${sidenavn(p.page)}`}
              >
                <span /><span /><span />
              </span>

              <span className="pagerow__name">{sidenavn(p.page)}</span>

              <Switch
                value={p.enabled}
                disabled={lockedOn}
                label={sidenavn(p.page)}
                onChange={() => {
                  if (lockedOn) return;
                  setPages(pages.map((x) =>
                    x.page === p.page ? { ...x, enabled: !x.enabled } : x));
                }}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
